import { MenuItem, Pagination, Select } from '@mui/material';
import { useActiveEntities } from 'hooks';
import useJobPolling from 'hooks/useJobPolling';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSearchParams } from 'routes';
import JobRow from '../JobStatusBoard/JobRow';
import StyledButton from '../StyledButton';
import { IGenericObject, IJob } from '../types';
import useStyles from './styles';

export interface IJobState {
  contents: IJob[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  numEntities: number;
  numPages: number;
  numQueued: number;
  numRunning: number;
  page: number;
  pageSize: number;
}

export const defaultJobState: IJobState = {
  contents: [],
  hasNextPage: false,
  hasPrevPage: false,
  numEntities: 0,
  numPages: 0,
  numQueued: 0,
  numRunning: 0,
  page: 1,
  pageSize: 5,
};

interface IJobPaginationProps {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize?: number;
  setPageSize?: React.Dispatch<React.SetStateAction<number>>;
  jobState: IJobState;
  size?: 'small' | 'medium' | 'large';
}

export const JobPagination = ({
  jobState,
  pageSize,
  setPageSize,
  page,
  setPage,
  size = 'large',
}: IJobPaginationProps) => {
  const inlayString = `Viewing Jobs: ${(jobState.page - 1) * jobState.pageSize + 1} - ${
    (jobState.page - 1) * jobState.pageSize + jobState.contents.length
  } of ${jobState.numEntities}`;
  return (
    <div
      style={{
        alignSelf: 'center',
        marginTop: size === 'small' ? 2 : 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {size === 'small' ? <h6>{inlayString}</h6> : <h5>{inlayString}</h5>}
        {pageSize && setPageSize && (
          <Select
            variant="standard"
            size="small"
            value={pageSize}
            renderValue={(value) => `${value} per page`}
            onChange={(e) => setPageSize(e.target.value as number)}
          >
            <MenuItem value={5}>5 per page</MenuItem>
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={20}>20 per page</MenuItem>
          </Select>
        )}
      </div>
      <Pagination
        color="primary"
        size={size}
        count={jobState.numPages}
        page={jobState.page}
        onChange={(e, value) => setPage(value)}
        disabled={page !== jobState.page}
        siblingCount={size !== 'large' ? 1 : 2}
        boundaryCount={size === 'small' ? 0 : 1}
      />
    </div>
  );
};

interface IJobMenuProps {
  actionButtonProps: (job: IJob) => IGenericObject;
  page?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  pageSize?: number;
  jobState?: IJobState;
  setJobState?: React.Dispatch<React.SetStateAction<IJobState>>;
  size?: 'small' | 'medium' | 'large';
}

const defaultPageSize = 10;

const JobMenu = ({
  actionButtonProps,
  page: propPage,
  setPage: propSetPage,
  pageSize: propPageSize,
  jobState: propJobState,
  setJobState: propSetJobState,
  size = 'small',
}: IJobMenuProps) => {
  const { classes } = useStyles();
  const { share } = getSearchParams();
  const {
    branch: { id },
  } = useActiveEntities();

  const isControlled = useMemo(() => propPage !== undefined, [propPage]);

  const [_page, _setPage] = useState(1);
  const [_jobState, _setJobState] = useState(defaultJobState);

  const { page, setPage, pageSize, jobState, setJobState } = useMemo(
    () => ({
      page: isControlled ? propPage! : _page,
      setPage: isControlled ? propSetPage! : _setPage,
      pageSize: propPageSize || defaultPageSize,
      jobState: isControlled ? propJobState! : _jobState,
      setJobState: isControlled ? propSetJobState! : _setJobState,
    }),

    [
      isControlled,
      propPage,
      propSetPage,
      propPageSize,
      propJobState,
      propSetJobState,
      _page,
      _setPage,
      _jobState,
      _setJobState,
    ]
  );

  const params = useMemo(
    () => ({ branch: id, page, pageSize, setJobState, share }),
    [id, page, pageSize, setJobState, share]
  );
  useJobPolling(params, 5000);

  // If user requests a page that no longer exist, go to last page
  useEffect(() => {
    if (page > jobState.page && !jobState.hasNextPage) {
      setPage(jobState.page);
    }
  }, [page, jobState, setPage]);

  const actionButton = useCallback(
    (job: IJob) => {
      const buttonProps = actionButtonProps(job);
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StyledButton
            type="button"
            min
            framed
            replaceSpinner
            dontDisableInReadOnly
            style={{ margin: 8 }}
            {...buttonProps}
          >
            <h6>{buttonProps.text || (job.dataArray ? 'Analyze' : 'No Data')}</h6>
          </StyledButton>
        </div>
      );
    },
    [actionButtonProps]
  );

  return (
    <div className={classes.interior}>
      <h3>Simulation Jobs</h3>
      {jobState.contents.map((job) => (
        <JobRow
          key={job.id}
          job={job}
          sortByBranch={true}
          small={true}
          rowOnClick={actionButtonProps(job).onClick}
          actionButton={actionButton}
        />
      ))}
      <JobPagination page={page} setPage={setPage} jobState={jobState} size={size} />
    </div>
  );
};

export default JobMenu;
